import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import i18n from 'modules/core/lib/i18n'
import { useCallback, useMemo, useRef } from 'react'
import { TicketsConnectionDocument, type TicketsConnectionQuery } from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { type FilterDefinition } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useMyResolverTeams } from 'modules/ticketing/hooks/useMyResolverTeams'
import { useResolverTeams } from 'modules/ticketing/hooks/useResolverTeams'
import { useTeamMembers } from 'modules/ticketing/hooks/useTeamMembers'
import {
  TicketingCategoriesForSelectorDocument,
  type TicketingCategoriesForSelectorQuery,
} from 'generated/resources/graphql'
import {
  type TicketNode,
  buildTableColumns,
  getSlaOptions,
  getSlaParamsForValue,
} from './ticketListConfig'
import {
  trackFilterTickets,
  trackSearchTickets,
  trackSortTickets,
} from 'modules/ticketing/lib/tracking'

interface TicketListProps {
  onOpenCreateDialog: () => void
  onItemClick: (item: TicketNode) => void
}

function buildFilters(
  teams: Array<{ label: string; value: string }>,
  members: Array<{ label: string; value: string }>,
  categories: Array<{ label: string; value: string }>
) {
  const filters: Record<string, FilterDefinition> = {
    status: {
      type: 'in' as const,
      label: i18n.t('ticketing.common.status'),
      options: {
        options: async () => [
          { label: i18n.t('ticketing.inbox.status.open'), value: 'open' },
          { label: i18n.t('ticketing.inbox.status.in_progress'), value: 'in_progress' },
          {
            label: i18n.t('ticketing.inbox.status.waiting_on_requestor'),
            value: 'waiting_on_requestor',
          },
          { label: i18n.t('ticketing.inbox.status.resolved'), value: 'resolved' },
          { label: i18n.t('ticketing.inbox.status.rejected'), value: 'rejected' },
        ],
      },
    },
    priority: {
      type: 'in' as const,
      label: i18n.t('ticketing.common.priority'),
      options: {
        options: async () => [
          { label: i18n.t('ticketing.inbox.priority.critical'), value: 'critical' },
          { label: i18n.t('ticketing.inbox.priority.high'), value: 'high' },
          { label: i18n.t('ticketing.inbox.priority.medium'), value: 'medium' },
          { label: i18n.t('ticketing.inbox.priority.low'), value: 'low' },
        ],
      },
    },
    sla_remaining: {
      type: 'in' as const,
      label: i18n.t('ticketing.inbox.filters.sla_remaining'),
      options: {
        options: async () => getSlaOptions(),
      },
    },
    assignee_team: {
      type: 'in' as const,
      label: i18n.t('ticketing.inbox.columns.assignee_team'),
      options: {
        options: async () => teams.map((t) => ({ label: t.label, value: String(t.value) })),
      },
    },
    assignee_member: {
      type: 'in' as const,
      label: i18n.t('ticketing.inbox.columns.assignee_member'),
      options: {
        options: async () => members.map((m) => ({ label: m.label, value: String(m.value) })),
      },
    },
    requester: {
      type: 'in' as const,
      label: i18n.t('ticketing.inbox.filters.requester'),
      options: {
        options: async () => members.map((m) => ({ label: m.label, value: String(m.value) })),
      },
    },
    ticket_category: {
      type: 'in' as const,
      label: i18n.t('ticketing.inbox.columns.category'),
      options: {
        options: async () => categories.map((c) => ({ label: c.label, value: String(c.value) })),
      },
    },
  }

  return filters
}

type FilterValues = {
  status?: string[]
  priority?: string[]
  sla_remaining?: string[]
  assignee_team?: string[]
  assignee_member?: string[]
  requester?: string[]
  ticket_category?: string[]
}

function buildVariables(
  search?: string,
  filters?: FilterValues,
  sortings?: Array<{ field: string; order: string }>
) {
  const slaValue = filters?.sla_remaining?.[0]

  return {
    first: 30,
    search: search?.trim() || undefined,
    ...(filters?.status?.length ? { statuses: filters.status } : {}),
    ...(filters?.priority?.length ? { priorities: filters.priority } : {}),
    ...(slaValue ? getSlaParamsForValue(slaValue) : {}),
    ...(filters?.assignee_team?.length ? { assigneeTeamIds: filters.assignee_team } : {}),
    ...(filters?.assignee_member?.length ? { assigneeMemberIds: filters.assignee_member } : {}),
    ...(filters?.requester?.length ? { requesterIds: filters.requester } : {}),
    ...(filters?.ticket_category?.length ? { ticketCategoryIds: filters.ticket_category } : {}),
    ...(sortings?.[0] ? { sort: sortings[0] } : {}),
  }
}

export const TicketList = ({ onOpenCreateDialog, onItemClick }: TicketListProps) => {
  const { teams } = useResolverTeams()
  const { teams: myTeams } = useMyResolverTeams()
  const { members } = useTeamMembers()
  const { employee } = useCurrentSession()

  const { data: categoriesData } = useQuery<
    TicketingCategoriesForSelectorQuery,
    Record<string, never>
  >(TicketingCategoriesForSelectorDocument, { fetchPolicy: 'cache-and-network' })
  const categories = useMemo(
    () =>
      (categoriesData?.ticketing?.ticketCategories ?? []).map((c) => ({
        label: c.name,
        value: String(c.id),
      })),
    [categoriesData]
  )

  const prevStateRef = useRef<{
    search?: string
    filters?: FilterValues
    sortings?: Array<{ field: string; order: string }>
  }>({})

  const trackListChanges = useCallback(
    ({
      search,
      filters,
      sortings,
    }: {
      search?: string
      filters?: FilterValues
      sortings?: Array<{ field: string; order: string }>
    }) => {
      const prev = prevStateRef.current

      // Track search
      if (search !== prev.search && search?.trim()) {
        trackSearchTickets()
      }

      // Track filter changes
      if (filters && prev.filters) {
        const filterKeys = [
          'status',
          'priority',
          'sla_remaining',
          'assignee_team',
          'assignee_member',
          'requester',
          'ticket_category',
        ] as const
        for (const key of filterKeys) {
          const prevVal = prev.filters[key]
          const newVal = filters[key]
          if (JSON.stringify(prevVal) !== JSON.stringify(newVal) && newVal?.length) {
            trackFilterTickets({ filter_type: key })
          }
        }
      }

      // Track sort changes
      if (sortings?.[0] && JSON.stringify(sortings[0]) !== JSON.stringify(prev.sortings?.[0])) {
        trackSortTickets({
          sort_field: sortings[0].field,
          sort_order: sortings[0].order,
        })
      }

      prevStateRef.current = { search, filters, sortings }
    },
    []
  )

  const handleItemClick = useCallback(
    (item: TicketNode) => {
      onItemClick(item)
    },
    [onItemClick]
  )

  const dataSourceOptions = useMemo(
    () => ({
      document: TicketsConnectionDocument,
      perPage: 30,
      paginationType: 'infinite-scroll' as const,
      fetchPolicy: 'cache-and-network' as const,
      sortings: {
        status: {
          label: i18n.t('ticketing.common.status'),
          sortBy: 'status',
        },
        priority: {
          label: i18n.t('ticketing.common.priority'),
          sortBy: 'priority',
        },
        slaDueAt: {
          label: i18n.t('ticketing.inbox.columns.sla_due_at'),
          sortBy: 'sla_due_at',
        },
        timeRemaining: {
          label: i18n.t('ticketing.inbox.columns.time_remaining'),
          sortBy: 'time_remaining',
        },
      },
      search: { enabled: true, placeholder: i18n.t('ticketing.inbox.search_placeholder') },
      filters: buildFilters(teams, members, categories),
      defaultFilters: { requester: [], assignee_member: [], assignee_team: [] },
      presets: [
        {
          label: i18n.t('ticketing.inbox.presets.all_tickets'),
          filter: { requester: [], assignee_member: [], assignee_team: [] },
        },
        {
          label: i18n.t('ticketing.inbox.presets.requested_by_me'),
          filter: { requester: [String(employee.id)], assignee_member: [], assignee_team: [] },
        },
        {
          label: i18n.t('ticketing.inbox.presets.assigned_to_me'),
          filter: { requester: [], assignee_member: [String(employee.id)], assignee_team: [] },
        },
        ...(myTeams.length > 0
          ? [
              {
                label: i18n.t('ticketing.inbox.presets.assigned_to_my_teams'),
                filter: {
                  requester: [],
                  assignee_member: [],
                  assignee_team: myTeams.map((t) => String(t.value)),
                },
              },
            ]
          : []),
      ],
      primaryActions: () => [
        {
          label: i18n.t('ticketing.inbox.create.new_ticket'),
          icon: Add,
          onClick: () => onOpenCreateDialog(),
        },
      ],
      mapVariables: ({
        search,
        filters,
        sortings,
      }: {
        search?: string
        filters?: FilterValues
        sortings?: Array<{ field: string; order: string }>
      }) => {
        trackListChanges({ search, filters, sortings })
        return buildVariables(search, filters, sortings)
      },
      mapData: (data: TicketsConnectionQuery) => data.ticketing.ticketsConnection.nodes,
      mapPagination: (data: TicketsConnectionQuery) => {
        const conn = data.ticketing.ticketsConnection
        return {
          total: conn.nodes.length,
          cursor: conn.pageInfo.endCursor ?? undefined,
          hasNextPage: conn.pageInfo.hasNextPage ?? false,
        }
      },
      itemOnClick: (item: TicketNode) => () => handleItemClick(item),
    }),
    [
      teams,
      myTeams,
      members,
      categories,
      handleItemClick,
      onOpenCreateDialog,
      employee,
      trackListChanges,
    ]
  )

  // TODO: integration test — filter options load after teams/members/categories resolve
  const source = useGraphqlDataCollectionSource(dataSourceOptions, [
    teams,
    myTeams,
    members,
    categories,
  ])

  const tableColumns = useMemo(() => buildTableColumns(), [])

  return (
    <OneDataCollection
      source={source}
      emptyStates={{
        'no-data': {
          title: i18n.t('ticketing.inbox.empty_state'),
          description: i18n.t('ticketing.inbox.empty_state_description'),
        },
        error: {
          title: i18n.t('ticketing.inbox.error_state_title'),
          description: i18n.t('ticketing.inbox.error_state_description'),
        },
      }}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: tableColumns,
          },
        },
      ]}
    />
  )
}
